import React, { useMemo, Fragment, useEffect } from 'react';

// Components
import { TablePagination } from 'components/third-party/ReactTable';

// Packages
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Table as MaterialTable,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    useFilters,
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable
} from 'react-table';

// Utils
import { renderFilterTypes, DefaultColumnFilter } from 'utils/react-table';

const Table = ({
    columns,
    data,
    onRowClick,
    pagination = true,
    externalGlobalFilter = '',
    initialSortBy = [],
    loadMore = false,
    hasMore = false,
    onLoadMore,
    loadingMore = false,
    disableSortBy = false,
    pageSize: initialPageSize = 10,
    maxHeight,
    // A fixed height instead of a ceiling: the block occupies the same space whether it holds two
    // rows or two hundred, so filtering does not make the page jump. `maxHeight` is unchanged for
    // every caller that already passes it.
    height,
    loading = false,
    minWidth,
    stickyHeader = false,
    // Moves pagination out of the table body and below the scrolling area, so it stays visible
    // instead of being the last thing you reach by scrolling. Off by default - every existing
    // caller keeps the in-body footer it has today.
    stickyFooter = false
}) => {
    const theme = useTheme();

    const filterTypes = useMemo(() => renderFilterTypes, []);
    const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
    const initialState = useMemo(
        () => ({
            filters: [],
            pageIndex: 0,
            pageSize: loadMore ? 10000 : initialPageSize,
            globalFilter: externalGlobalFilter || '',
            sortBy: initialSortBy
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        gotoPage,
        setPageSize,
        state: { pageIndex, pageSize, globalFilter: internalGlobalFilter },
        setGlobalFilter
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState,
            filterTypes,
            autoResetGlobalFilter: false,
            disableSortBy,
            // Toggle only asc <-> desc. Without this, a third header click clears the sort
            // and rows silently fall back to their natural data order.
            disableSortRemove: true
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination
    );

    useEffect(() => {
        if (externalGlobalFilter !== internalGlobalFilter) {
            setGlobalFilter(externalGlobalFilter || '');
        }
    }, [externalGlobalFilter, internalGlobalFilter, setGlobalFilter]);

    const handleRowClick = rowData => {
        if (onRowClick) onRowClick(rowData);
    };

    const visibleRows = pagination || loadMore ? page : rows;
    const showFooterBelow = stickyFooter && pagination && !loadMore;

    const scrollSx = height
        ? { flex: 1, minHeight: 0, overflowY: 'auto' }
        : maxHeight
          ? { maxHeight, overflowY: 'auto' }
          : undefined;

    return (
        <Stack sx={{ height: height ?? '100%' }}>
            <Box sx={scrollSx}>
                <MaterialTable
                    {...getTableProps()}
                    stickyHeader={stickyHeader}
                    sx={{ minWidth }}
                >
                    <TableHead sx={{ borderTopWidth: 1 }}>
                        {headerGroups.map(headerGroup => {
                            const { key, ...headerGroupProps } =
                                headerGroup.getHeaderGroupProps();
                            return (
                                <TableRow key={key} {...headerGroupProps}>
                                    {headerGroup.headers.map(column => {
                                        const { key, ...columnProps } =
                                            column.getHeaderProps([
                                                { className: column.className },
                                                column.getSortByToggleProps()
                                            ]);
                                        const canSort = column.canSort;
                                        return (
                                            <TableCell
                                                key={key}
                                                {...columnProps}
                                                sx={{ width: 'auto' }}
                                            >
                                                {canSort ? (
                                                    <TableSortLabel
                                                        active={column.isSorted}
                                                        direction={
                                                            column.isSorted &&
                                                            column.isSortedDesc
                                                                ? 'desc'
                                                                : 'asc'
                                                        }
                                                    >
                                                        {column.render('Header')}
                                                    </TableSortLabel>
                                                ) : (
                                                    column.render('Header')
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableHead>

                    <TableBody {...getTableBodyProps()}>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : data && data.length === 0 ? (
                            <TableRow colSpan="12">
                                <TableCell>
                                    <Typography
                                        variant="h5"
                                        component="span"
                                        color="text.secondary"
                                        sx={{
                                            width: '100%',
                                            fontWeight: 400,
                                            fontStyle: 'Regular',
                                            fontSize: '13px'
                                        }}
                                    >
                                        No results found....
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {visibleRows.map(row => {
                                    prepareRow(row);
                                    const { key, ...rowProps } = row.getRowProps();
                                    return (
                                        <Fragment key={key}>
                                            <TableRow
                                                {...rowProps}
                                                onClick={() =>
                                                    handleRowClick(row.original)
                                                }
                                                sx={{
                                                    cursor: 'pointer',
                                                    textTransform: 'capitalize',
                                                    bgcolor: row.isSelected
                                                        ? alpha(
                                                              theme.palette.primary
                                                                  .lighter,
                                                              0.35
                                                          )
                                                        : 'inherit'
                                                }}
                                            >
                                                {row.cells.map(cell => {
                                                    const { key, ...cellProps } =
                                                        cell.getCellProps([
                                                            {
                                                                className:
                                                                    cell.column.className
                                                            }
                                                        ]);
                                                    return (
                                                        <TableCell
                                                            key={key}
                                                            {...cellProps}
                                                        >
                                                            {cell.render('Cell')}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </Fragment>
                                    );
                                })}

                                {/* ── Default pagination ──────────────────── */}
                                {pagination && !loadMore && !showFooterBelow && (
                                    <TableRow>
                                        <TableCell
                                            sx={{ p: 2 }}
                                            colSpan={columns.length || 7}
                                        >
                                            <TablePagination
                                                gotoPage={gotoPage}
                                                rows={rows}
                                                setPageSize={setPageSize}
                                                pageIndex={pageIndex}
                                                pageSize={pageSize}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}

                                {loadMore && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length || 7}
                                            align="center"
                                            sx={{ py: 2, borderBottom: 'none' }}
                                        >
                                            {hasMore ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={onLoadMore}
                                                    disabled={loadingMore}
                                                    startIcon={
                                                        loadingMore && (
                                                            <CircularProgress size={14} />
                                                        )
                                                    }
                                                >
                                                    {loadingMore
                                                        ? 'Loading…'
                                                        : 'Load more'}
                                                </Button>
                                            ) : (
                                                data.length > 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        All {data.length} records loaded
                                                    </Typography>
                                                )
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </MaterialTable>
            </Box>

            {showFooterBelow && (
                <Box
                    sx={{
                        flexShrink: 0,
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <TablePagination
                        gotoPage={gotoPage}
                        rows={rows}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                    />
                </Box>
            )}
        </Stack>
    );
};

export default Table;
